import { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { localStorage, ViewerContext } from "dash-ui";
import { useMfaAuthenticationMutation } from "gql/generated.graphql";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { RoutesPath } from "flow-components/routes/routes";

const confirmMfaFormSchema = z.object({
  code: z.string().min(1, "Code is required."),
});

type ConfirmMFAFormData = z.infer<typeof confirmMfaFormSchema>;

export function ConfirmMfaCodeForm() {
  // React states
  const [errorCount, setErrorCount] = useState(0);

  // Hooks
  const confirmMfaForm = useForm<ConfirmMFAFormData>({
    resolver: zodResolver(confirmMfaFormSchema),
  });
  const navigate = useNavigate();
  const { setTokens } = useContext(ViewerContext);

  // Queries and Mutations
  const [mfaAuthMutation] = useMfaAuthenticationMutation();

  // Handle confirmation
  async function confirm(data: ConfirmMFAFormData) {
    await mfaAuthMutation({ variables: { token: data.code } })
      .then((response) => {
        const accessToken = response.data?.mfaAuthentication.accessToken;
        const refreshToken = response.data?.mfaAuthentication.refreshToken;
        const refreshTokenExpiry =
          response.data?.mfaAuthentication.refreshTokenExpiry;

        if (!accessToken || !refreshToken) {
          confirmMfaForm.setError("code", {
            message:
              "Error: Access token not provided. Please contact support.",
          });
          return;
        }
        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || "");

        setTokens?.({
          accessToken: accessToken,
          refreshToken: refreshToken,
          refreshTokenExpiry: refreshTokenExpiry,
        });
      })
      .catch((error: ApolloError) => {
        if (error.message === "Unauthorized") return navigate(RoutesPath.SignIn);
        confirmMfaForm.setError("code", { message: error.message });
        setErrorCount(errorCount + 1);
      });
  }

  // HTML Element
  return (
    <div className="flex flex-col items-center justify-center h-full w-full">
      <form
        className="flex flex-col w-full max-w-96 p-4"
        onSubmit={confirmMfaForm.handleSubmit(confirm)}
      >
        <h1 className="text-2xl font-bold font-[montserrat]">
          Multi Factor Authentication
        </h1>
        <p className="text-slate-500 mt-1">
          Please, confirm the code on your Authenticator App.
        </p>
        <label className="text-sm text-slate-500 mt-4">Code</label>
        <input
          className={`h-10 rounded border px-4 ${
            confirmMfaForm.formState.errors.code
              ? "border-red-600 bg-red-50"
              : "border-slate-300"
          }`}
          placeholder="123456"
          {...confirmMfaForm.register("code")}
          autoFocus
        />
        <span className="text-sm text-red-600">
          {confirmMfaForm.formState.errors.code?.message}
        </span>
        <button className="h-10 rounded bg-sky-600 text-white mt-4">
          Confirm
        </button>
        {errorCount > 5 && (
          <p className="border rounded bg-sky-50 border-sky-600 text-sky-700 px-4 py-2 mt-8">
            If your code is not working, please contact support.
          </p>
        )}
      </form>
    </div>
  );
}
